'use client';

import { useFetcher } from '@remix-run/react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  Loader2,
  MapPin,
  ShieldCheck,
} from 'lucide-react';
import Logo from '~/assets/Logo';
import { ChannelsQuery } from '~/generated/graphql';
import {
  generateDays,
  generateMonths,
  generateYears,
  validateAgeVerificationForm,
} from '~/utils/age-verification';
import { cn } from '~/utils/class-names';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface Props {
  channels: ChannelsQuery['publicChannels']['items'];
}

interface FormData {
  province: string;
  month: string;
  day: string;
  year: string;
}

// Shared styling for the dark, luxe select controls.
const selectClass = (hasError: boolean, isEmpty: boolean) =>
  cn(
    'w-full appearance-none rounded-xl border bg-none px-4 py-3 pr-10 text-sm outline-none transition-colors',
    '[&>option]:bg-[#2A1A40] [&>option]:text-white',
    'focus:ring-2',
    isEmpty ? 'text-white/50' : 'text-white',
    hasError
      ? 'border-red-400/50 bg-red-500/10 focus:border-red-400/70 focus:ring-red-500/20'
      : 'border-white/15 bg-white/[0.07] hover:border-white/25 hover:bg-white/[0.1] focus:border-amber-200/60 focus:ring-amber-200/20',
  );

// Wraps a native <select> and overlays a gold chevron (native arrow removed).
function SelectShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/50" />
    </div>
  );
}

export default function VerificationPopup({ channels }: Props) {
  const [formData, setFormData] = useState<FormData>({
    province: '',
    month: '',
    day: '',
    year: '',
  });
  const [fieldErrors, setFieldErrors] = useState<{
    province?: string;
    dateOfBirth?: string;
  }>({});

  const fetcher = useFetcher<{ success: boolean }>();
  const isSubmitting = fetcher.state !== 'idle';

  useEffect(() => {
    if (fetcher.data?.success) {
      window.location.reload();
    }
  }, [fetcher.data]);

  // Generate dropdown options
  const months = generateMonths();
  const days = generateDays();
  const years = generateYears();

  const dobFields: {
    key: 'month' | 'day' | 'year';
    placeholder: string;
    options: { value: string; label: string }[];
  }[] = [
    { key: 'month', placeholder: 'Month', options: months },
    { key: 'day', placeholder: 'Day', options: days },
    { key: 'year', placeholder: 'Year', options: years },
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (field === 'province' && fieldErrors.province) {
      setFieldErrors((prev) => ({ ...prev, province: undefined }));
    } else if (
      ['month', 'day', 'year'].includes(field) &&
      fieldErrors.dateOfBirth
    ) {
      setFieldErrors((prev) => ({ ...prev, dateOfBirth: undefined }));
    }
  };

  const handleSubmit = () => {
    // Validate form
    const validation = validateAgeVerificationForm(formData);

    if (!validation.isValid) {
      setFieldErrors(validation.fieldErrors);
      return;
    }

    // If validation passes, proceed with form submission
    fetcher.submit(
      { channelToken: formData.province },
      {
        method: 'post',
        action: '/api/select-channel',
      },
    );
  };

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        className="overflow-hidden shadow-2xl shadow-black/70"
        style={{
          maxWidth: 'min(420px, calc(100% - 2rem))',
          padding: 0,
          borderRadius: 28,
          border: '1px solid rgba(255,255,255,0.12)',
          background:
            'radial-gradient(120% 90% at 50% 0%, #3B2458 0%, #2C1B44 45%, #201634 100%)',
        }}
      >
        <div className="max-h-[92vh] overflow-y-auto">
          {/* Gold hairline accent flush to the top edge */}
          <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-amber-200/70 to-transparent" />

          <div className="px-7 pb-8 pt-8 sm:px-8">
            <DialogHeader className="text-center">
              {/* Logo emblem — the black-backed mark blends into a framed plate */}
              <div className="mx-auto mb-5 w-fit rounded-2xl border border-white/5 bg-black p-3 ring-1 ring-amber-200/10">
                <Logo className="mx-auto h-16 w-auto sm:h-[76px]" />
              </div>

              <div className="mx-auto mb-4 flex w-fit items-center gap-1.5 rounded-full border border-amber-200/25 bg-amber-200/[0.06] px-3 py-1">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-200" />
                <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-amber-100/80">
                  Restricted Access
                </span>
              </div>

              <DialogTitle className="text-center text-xl font-semibold tracking-wide text-white">
                Age Verification
              </DialogTitle>

              <DialogDescription className="mx-auto mt-2 max-w-xs text-center text-[13px] leading-relaxed text-white/55">
                Provincial taxes now apply. Please select your province
                carefully to ensure accurate pricing at checkout.
              </DialogDescription>
            </DialogHeader>

            {/* Divider */}
            <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

            {/* Province */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-amber-100/70">
                <MapPin className="h-3.5 w-3.5 text-amber-200/70" />
                Province
              </label>
              <SelectShell>
                <select
                  className={selectClass(
                    !!fieldErrors.province,
                    !formData.province,
                  )}
                  value={formData.province}
                  onChange={(e) =>
                    handleInputChange('province', e.target.value)
                  }
                  name="province"
                  aria-invalid={!!fieldErrors.province}
                >
                  <option value="" disabled>
                    Choose your province
                  </option>
                  {channels.map((channel) => (
                    <option key={channel.code} value={channel.token}>
                      {channel.code}
                    </option>
                  ))}
                </select>
              </SelectShell>
              {fieldErrors.province && (
                <p className="text-xs text-red-300/90">
                  {fieldErrors.province}
                </p>
              )}
            </div>

            {/* Date of birth */}
            <div className="mt-5 space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-amber-100/70">
                <CalendarDays className="h-3.5 w-3.5 text-amber-200/70" />
                Date of Birth
              </label>
              <div className="grid grid-cols-3 gap-2.5">
                {dobFields.map((field) => (
                  <SelectShell key={field.key}>
                    <select
                      className={selectClass(
                        !!fieldErrors.dateOfBirth,
                        !formData[field.key],
                      )}
                      value={formData[field.key]}
                      onChange={(e) =>
                        handleInputChange(field.key, e.target.value)
                      }
                      aria-invalid={!!fieldErrors.dateOfBirth}
                    >
                      <option value="" disabled>
                        {field.placeholder}
                      </option>
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </SelectShell>
                ))}
              </div>
              {fieldErrors.dateOfBirth && (
                <p className="text-xs text-red-300/90">
                  {fieldErrors.dateOfBirth}
                </p>
              )}
            </div>

            {/* Consent */}
            <p className="mt-5 text-center text-[11px] leading-relaxed text-white/40">
              By entering, I confirm the details above are accurate and that I
              am of legal age to purchase vaping products in my province.
            </p>

            {/* CTA */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="group relative mt-5 flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#4A0D82] via-[#7B2CBF] to-[#4A0D82] text-sm font-semibold uppercase tracking-[0.15em] text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:shadow-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {/* Gold sheen sweep on hover */}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  Enter Site
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>

            <p className="mt-4 text-center text-[10px] uppercase tracking-[0.3em] text-amber-100/30">
              KTM Vape · Luxury in every puff
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
